/*
 * File: /src/store/modules/auth.js
 * Created Date: Sunday March 17th 2019
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Tuesday, 2nd April 2019 11:13:54 pm
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */

import { authedAxios, publicAxios } from 'src/shared/axiosWrapper';
import { AB_PROFILE_PHOTO_URL } from 'src/constants';

// This sets the base URL and header via the mixin setup
const jwt = localStorage.getItem('s2-access-token') || undefined;

// initial state
const state = {
  // I tire of changing it in so many places.
  /* move this to app or somewhere else as it is not used that I can find
  companyName: 'SendSquared',
  companyUrl: 'sendsquared.com',
  */

  authAccount: undefined,
  /*
  authAccount: {
    // companyId: 1
    // name: "Nicolas Wegener"
    // userId:1
    // username: "wegenern@gmail.com"
    // profilePhoto: ''
    // companies: [], // All accounts this user can see
  },
  */
  userData: {},
  acls: [],
  brands: [], // Set of brands the user is Auth/Assigned to; from BE
  jwt: jwt,
  agencyPrimaryAccount: {
    id: 0,
    name: 0
  },
  // TODO: This should be moved elsewhere as its not auth related
  brandIds: undefined, // Global brand filters, unset means all
  // Once its set it will filter to those ids, might be empty for none
  longTokenMode: false, // Long lasting tokens for realtime data. Very limited scope on what they can see to prevent data leaks
};

// getters
const getters = {


  getCompanyName: state => {
    if (!state.authAccount?.companies) {
      return;
    }
    const company = state.authAccount.companies.find(c => c.id === state.authAccount.companyId);
    return company?.name;
  },
  getCompanyUuid: state => {
    if (!state.authAccount?.companies) {
      return;
    }
    const company = state.authAccount.companies.find(c => c.id === state.authAccount.companyId);
    return company?.uuid;
  },

  getEmulateMode: state => state.authAccount ? state.authAccount.sourceId : undefined,

  getAgencyMode: state => {
    return state.agencyPrimaryAccount && state.authAccount ? state.agencyPrimaryAccount === state.authAccount.companyId : false;
  },
  getAgencyPrimary: state => state.agencyPrimaryAccount,

  getLoginStatus: state => (state.jwt ? true : false),
  getJwtString: state => state.jwt,
  getLoginAccount: state => state.authAccount,
  getLoginAccountName: state => state.authAccount ? state.authAccount.name : undefined,
  getAccountUsername: state => state.authAccount ? state.authAccount.username : undefined,
  getAccountCompany: state => state.authAccount ? state.authAccount.companyId : undefined,
  getUserData: state => state.userData,
  getProfilePhoto: (state, getters) =>
    getters.getProfilePhotoURL + getters.getAccountProfilePhoto,
  getAccountProfilePhoto: state => {
    if (
      state.authAccount !== undefined &&
      state.authAccount.profilePhoto !== undefined &&
      state.authAccount.profilePhoto.length
    ) {
      return state.authAccount.profilePhoto;
    }
    return 'default-user-profile.png';
  },
  getProfilePhotoURL: () => AB_PROFILE_PHOTO_URL,
  getCompanyById: state => id => {
    for (const inc in state?.authAccount?.companies) {
      if (id === state?.authAccount?.companies[inc].id) {
        return state.authAccount.companies[inc];
      }
    }
  },
  // TODO Kill this one or at least reanme it
  getAuthCompanies: state => state?.authAccount?.companies,

  /**
   * Get ACLs
   * @param {*} state
   * @returns
   */
  getAcls: state => aclCheck => {
    return state.acls.find(({ permission }) => permission === aclCheck)
      ? true
      : false;
  },
  getAuthBrands: state => state?.brands,
  getGlobalBrandIds: state => {
    return state.brandIds;
  },
  getLongTokenMode: state => {
    return state.longTokenMode;
  },
};

// actions
const actions = {
  setLoggedIn({ commit }, authVals) {
    // commit('setAuthStatus', true);
    commit('setAuthAccount', authVals.authAccount);
    commit('setAuthAcls', authVals.authAcls);
    commit('setAuthBrands', authVals.authBrands);
  },

  /**
   * Login the User
   * @param authData username and password combination
   */
  processLogin({ commit }, authData) {
    return new Promise((resolve, reject) => {

      publicAxios
        .post('/v1/login', {
          username: authData.username,
          password: authData.password,
          full_acls: true,
        })
        .then(response => {
          const data = response.data;
          localStorage.setItem('s2-access-token', data.jwt);
          localStorage.setItem('s2-refresh-token', data.refreshToken);
          commit('setJwt', data.jwt);

          // This should only be updated when getting a new token
          if (data.companies.length > 0) {
            // TODO: DO we really need this?
            commit('setAgencyParentAccount', {
              id: data.companyId,
              name: data.companyName
            });
          }

          commit('setAuthAccount', {
            username: authData.username,
            name: data.name,
            userId: data.userId,
            companyId: data.companyId,
            profilePhoto: data.profilePhoto,
            companies: data.companies,
          });
          // This needs to be a defined model, overwrite prone
          commit('setUserData', {
            lastAirbnbSeen: data.lastAirbnbSeen,
            lastSmsSeen: data.lastSmsSeen,
            lastMessageSeen: data.lastMessageSeen,
            lastVoicemailSeen: data.lastVoicemailSeen,
            lastWhatsappSeen:state.userData.lastWhatsappSeen,
          });
          // Add Acls - This will need to be checked at routes
          //  - also lot's of back and forth, as this is easily falsified.
          commit('setAuthAcls', data.acls);
          commit('setAuthBrands', data.brands);

          // Set Global Brands Ids from defaults
          commit('setGlobalBrandIds', data.defaultBrands);


          resolve({ success: 1 });
        })
        .catch(err => {
          console.log('error loging in', err);
          reject(err);
        });
    });
  },

  /* Replace the current stored refreshToken with a long duration token
   * This will log the user out essentially and will require a new token from an
   * authorized user after the refresh token expires
   */
  setLongToken: ( { commit }, data) => {
      localStorage.setItem('s2-access-token', data.token);
      localStorage.setItem('s2-refresh-token', data.refreshToken);
      commit('setJwt', data.token);
      commit('setAuthAccount', undefined);
      commit('setAuthAcls', []);
      commit('setAgencyParentAccount', {});
      // commit('setAgencyMode', false);
      commit('setLongTokenMode', true);
  },

  /**
   * Emulate Users Session
   * @param data emulated token object
   */
  emulate: ({ commit }, data) => {
    localStorage.setItem('s2-access-token', data.jwt);
    localStorage.setItem('s2-refresh-token', data.refreshToken);
    commit('setJwt', data.jwt);
    // This should all come from the JWT. Why do we sent it twice?
    commit('setAuthAccount', {
      username: data.email,
      name: data.name,
      userId: data.userId,
      companyId: data.companyId,
      profilePhoto: data.profilePhoto,
      sourceId: data.sourceId, // Will be undefined on stop emulation
      companies: data.companies,
    });
    // TODO: Validate that Acl data is here
    commit('setAuthAcls', data.acls);
    commit('setAuthBrands', data.brands);
    commit('setUserData', {
      lastSmsSeen: data.lastSmsSeen,
      lastMessageSeen: data.lastMessageSeen,
      lastVoicemailSeen: data.lastVoicemailSeen,
      lastWhatsappSeen: data.lastWhatsappSeen,
    });
  },

  setAgency: ({ commit, state }, data) => {
    localStorage.setItem('s2-access-token', data.jwt);
    commit('setJwt', data.jwt);
    // Just update the parts that change for an agency,
    const authAccount = Object.assign({}, state.authAccount);

    // Just replace the specific attrs that change for agency
    authAccount.companyId = data.companyId;
    authAccount.userId = data.userId;
    authAccount.name = data.name;
    commit('setAuthAccount', authAccount);
    commit('setAuthAcls', data.acls);
    commit('setAuthBrands', data.brands);
  },

  logout({ commit }) {
    localStorage.removeItem('s2-access-token');
    localStorage.removeItem('s2-refresh-token');
    commit('setJwt', undefined);
    commit('setAuthAccount', undefined);
    commit('setAuthAcls', []);
    commit('setAgencyParentAccount', {});
    localStorage.clear();
  },

  async updatePhoto({ commit, state }, meta) {
    return new Promise((resolve, reject) => {
      commit('setProfilePhoto', meta.url);
      resolve({});
    });
  },

  // These need to be moved to a user store, odd for now as they are nested and not defined specifically
  // So we only want to update one without crush the entire object
  updateLastAirbnbSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: lastId,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  updateLastSmsSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: lastId,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  updateLastWhatsappSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen: lastId,
    });
  },
  updateLastMessageSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: lastId,
      lastVoicemailSeen:state.userData.lastVoicemailSeen,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  updateLastVoicemailSeen({ commit, state }, lastId) {
    commit('setUserData', {
      lastAirbnbSeen: state.userData.lastAirbnbSeen,
      lastSmsSeen: state.userData.lastSmsSeen,
      lastMessageSeen: state.userData.lastMessageSeen,
      lastVoicemailSeen: lastId,
      lastWhatsappSeen:state.userData.lastWhatsappSeen,
    });
  },
  setGlobalBrandIds({ commit }, brandIds) {
    commit('setGlobalBrandIds', brandIds);
  },
};

// mutations
const mutations = {
  setLongTokenMode(state, mode) {
    state.longTokenMode = mode ? true : false;
  },
  setAuthAccount(state, authData) {
    localStorage.setItem('s2-auth-account', authData ? JSON.stringify(authData) : authData);

    state.authAccount = Object.assign({}, authData);
  },
  setAuthAcls(state, acls) {
    localStorage.setItem('s2-auth-acls', acls ? JSON.stringify(acls) : []);
    state.acls = Object.assign([], acls);
  },
  setAuthBrands(state, brands) {
    localStorage.setItem('s2-auth-brands', brands ? JSON.stringify(brands) : []);
    state.brands = Object.assign([], brands);
  },
  setProfilePhoto(state, url) {
    const obj = Object.assign({}, state.authAccount);
    obj.profilePhoto = url;
    state.authAccount = obj;
  },
  setJwt(state, jwt) {
    state.jwt = jwt;
  },
  setUserData(state, meta) {
    state.userData = Object.assign({}, meta);
  },
  // this is in the admin... might want to move this out of here.
  /*
  setCompanyAccounts(state, meta) {
    state.companyAccounts = Object.assign([], meta);
  },
  */
  setAgencyParentAccount(state, meta) {
    state.agencyPrimaryAccount = Object.assign({}, meta);
  },
  setGlobalBrandIds(state, ids) {
    // Expect an array for a filter, might be empty.
    // Or null for no brands filtered, all available to user
    state.brandIds = ids;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
